import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useCurrency, CURRENCIES } from '../../contexts/CurrencyContext';
import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  useTheme,
  TextField,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Drawer,
  useMediaQuery,
  Badge,
  Avatar,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  AttachMoney as CurrencyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  WarningAmber as WarningIcon,
  Brightness4 as ThemeAutoIcon,
  Fingerprint as FingerprintIcon,
  Face as FaceIcon,
  ImportExport as ImportExportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import ImportWizard from '../Import/ImportWizard';
import ExportButton from '../Import/ExportButton';

// API Configuration - Use consistent base URL pattern
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout, biometric } = useAuth();
  const { themeMode, toggleTheme } = useThemeContext();
  const { currency, updateCurrency } = useCurrency();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Section state
  const [activeSection, setActiveSection] = useState('preferences');
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currencyDialogOpen, setCurrencyDialogOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Biometric enrollment from Settings
  const [bioDialogOpen, setBioDialogOpen] = useState(false);
  const [bioPassword, setBioPassword] = useState('');
  const [bioLoading, setBioLoading] = useState(false);
  const [bioError, setBioError] = useState('');

  // Import wizard
  const [importWizardOpen, setImportWizardOpen] = useState(false);

  // "At a Glance" dashboard widgets visibility (default: off)
  const [showAtAGlance, setShowAtAGlance] = useState(() => {
    try {
      return localStorage.getItem('sk_show_at_a_glance') === 'true';
    } catch (_) {
      return false;
    }
  });
  
  // Currency picker state
  const [currencySearch, setCurrencySearch] = useState('');
  
  // Notification preferences (loaded from backend)
  const [notifPrefs, setNotifPrefs] = useState({
    email: true, push: true, budgetAlerts: true,
    securityAlerts: true, insightAlerts: true, spendingAlerts: true,
    agentAlerts: true, weeklyReport: true
  });
  const [notifPrefsLoaded, setNotifPrefsLoaded] = useState(false);

  // Fetch notification preferences on mount
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE_URL}/notifications/preferences`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data) {
          setNotifPrefs(res.data);
          setNotifPrefsLoaded(true);
        }
      })
      .catch(() => {});
  }, []);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ];
  
  // Get current currency info
  const currentCurrencyInfo = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
  
  // Filter currencies based on search
  const filteredCurrencies = CURRENCIES.filter(c =>
    c.name.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(currencySearch.toLowerCase())
  );

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('soldikeeper-language', newLanguage);
    
    // Persist to backend
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE_URL}/user-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ language: newLanguage })
      }).catch(() => {});
    }
    
    const langName = languages.find(l => l.code === newLanguage)?.name || newLanguage;
    setSnackbar({ open: true, message: `Language changed to ${langName}`, severity: 'success' });
  };
  
  const handleCurrencySelect = async (currencyCode) => {
    await updateCurrency(currencyCode);
    const selectedCurrency = CURRENCIES.find(c => c.code === currencyCode);
    setCurrencyDialogOpen(false);
    setCurrencySearch('');
    setSnackbar({ 
      open: true, 
      message: `Currency updated to ${selectedCurrency.name} (${selectedCurrency.symbol})`, 
      severity: 'success' 
    });
  };
  
  const handleNotificationToggle = async (key) => {
    const newValue = !notifPrefs[key];
    const updated = { ...notifPrefs, [key]: newValue };
    setNotifPrefs(updated);

    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(`${API_BASE_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ [key]: newValue })
      });
      const data = await resp.json();
      if (data.success) {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        setSnackbar({ open: true, message: `${label} ${newValue ? 'enabled' : 'disabled'}`, severity: 'success' });
      } else {
        setNotifPrefs(notifPrefs); // rollback
        setSnackbar({ open: true, message: 'Failed to update preference', severity: 'error' });
      }
    } catch {
      setNotifPrefs(notifPrefs); // rollback
      setSnackbar({ open: true, message: 'Network error updating preference', severity: 'error' });
    }
  };

  // Settings sections
  const settingsSections = [
    {
      id: 'preferences',
      title: 'Preferences',
      icon: <SettingsIcon />
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: <PaletteIcon />
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <NotificationsIcon />
    },
    {
      id: 'account',
      title: 'Account',
      icon: <AccountCircleIcon />
    },
    {
      id: 'security',
      title: 'Security',
      icon: <SecurityIcon />
    },
    {
      id: 'data',
      title: 'Import & Export',
      icon: <ImportExportIcon />
    },
    {
      id: 'danger',
      title: 'Danger Zone',
      icon: <WarningIcon />,
      danger: true
    }
  ];

  const handleDeleteAccount = async () => {
    // Validate email confirmation
    if (confirmEmail.toLowerCase() !== user?.email?.toLowerCase()) {
      setDeleteError(t('settings.emailMismatch', 'Email does not match your account email'));
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/user-preferences/account`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Account deleted successfully - logout and redirect
      setSnackbar({
        open: true,
        message: t('settings.accountDeleted', 'Your account has been successfully deleted'),
        severity: 'success'
      });

      // Wait a moment for the user to see the message, then logout
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(
        error.response?.data?.message || 
        t('settings.deleteError', 'Failed to delete account. Please try again or contact support.')
      );
      setIsDeleting(false);
    }
  };

  // Handle biometric enrollment from Settings with password confirmation
  const handleBioEnroll = async () => {
    if (!bioPassword || bioPassword.length < 6) {
      setBioError(t('biometric.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }
    setBioLoading(true);
    setBioError('');
    try {
      await biometric.enrollBiometric(user.email, bioPassword);
      setBioDialogOpen(false);
      setBioPassword('');
      setSnackbar({ open: true, message: t('biometric.enrolled', 'Biometric login enabled!'), severity: 'success' });
    } catch (err) {
      setBioError(err.message || t('biometric.enrollFailed', 'Failed to enable biometric'));
    } finally {
      setBioLoading(false);
    }
  };
  
  // Render content based on active section
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'preferences':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Preferences
            </Typography>
            
            {/* Currency Selector */}
            <Card 
              elevation={0} 
              sx={{ 
                mb: 3, 
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Currency
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose your preferred currency for transactions and budgets
                    </Typography>
                  </Box>
                  <CurrencyIcon color="action" />
                </Box>
                
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default', 
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3" sx={{ fontSize: '2rem' }}>
                      {currentCurrencyInfo.flag}
                    </Typography>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {currentCurrencyInfo.code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentCurrencyInfo.name} · {currentCurrencyInfo.symbol}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label="Current" 
                    color="primary" 
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => setCurrencyDialogOpen(true)}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5
                  }}
                >
                  Change Currency
                </Button>
              </CardContent>
            </Card>
            
            {/* Language Selector */}
            <Card 
              elevation={0} 
              sx={{ 
                mb: 3, 
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Language
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose your preferred language for the interface
                    </Typography>
                  </Box>
                  <LanguageIcon color="action" />
                </Box>
                
                <FormControl fullWidth>
                  <Select
                    value={i18n.language}
                    onChange={handleLanguageChange}
                    sx={{ 
                      '& .MuiSelect-select': { 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        py: 1.5
                      }
                    }}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>
                            {lang.flag}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {lang.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Box>
        );
        
      case 'appearance':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Appearance
            </Typography>
            
            {/* Theme Selector */}
            <Card 
              elevation={0} 
              sx={{ 
                mb: 3, 
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Theme Mode
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card
                      onClick={themeMode === 'dark' ? null : toggleTheme}
                      sx={{
                        cursor: 'pointer',
                        border: `2px solid ${themeMode === 'dark' ? theme.palette.primary.main : theme.palette.divider}`,
                        position: 'relative',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <DarkModeIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Dark
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Easy on the eyes
                        </Typography>
                        {themeMode === 'dark' && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Card
                      onClick={themeMode === 'light' ? null : toggleTheme}
                      sx={{
                        cursor: 'pointer',
                        border: `2px solid ${themeMode === 'light' ? theme.palette.primary.main : theme.palette.divider}`,
                        position: 'relative',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <LightModeIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Light
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bright and clear
                        </Typography>
                        {themeMode === 'light' && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Color Swatches */}
            <Card 
              elevation={0} 
              sx={{ 
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Color Accents
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: 2, 
                        bgcolor: 'primary.main',
                        mb: 1,
                        border: `2px solid ${theme.palette.divider}`
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      Primary
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: 2, 
                        bgcolor: 'secondary.main',
                        mb: 1,
                        border: `2px solid ${theme.palette.divider}`
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      Secondary
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: 2, 
                        bgcolor: 'success.main',
                        mb: 1,
                        border: `2px solid ${theme.palette.divider}`
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      Success
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: 2, 
                        bgcolor: 'error.main',
                        mb: 1,
                        border: `2px solid ${theme.palette.divider}`
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      Error
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Dashboard layout — At a Glance widgets toggle */}
            <Card elevation={0} sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      "At a Glance" widgets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Show the pinnable quick-stat tiles (Spending Today, Budget Health, Upcoming Bills, Savings Progress) at the top of your dashboard.
                    </Typography>
                  </Box>
                  <Switch
                    checked={showAtAGlance}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setShowAtAGlance(next);
                      try {
                        localStorage.setItem('sk_show_at_a_glance', next ? 'true' : 'false');
                        window.dispatchEvent(new Event('sk:atAGlanceChanged'));
                      } catch (_) { /* ignore */ }
                    }}
                    color="primary"
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
        
      case 'notifications':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Notifications
            </Typography>
            
            {/* Delivery Channels */}
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Delivery Channels
            </Typography>
            
            <Card elevation={0} sx={{ mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Email Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive transaction alerts and monthly summaries via email
                    </Typography>
                  </Box>
                  <Switch
                    checked={notifPrefs.email}
                    onChange={() => handleNotificationToggle('email')}
                    color="primary"
                  />
                </Box>
              </CardContent>
            </Card>
            
            <Card elevation={0} sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Push Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get instant updates on your mobile device
                    </Typography>
                  </Box>
                  <Switch
                    checked={notifPrefs.push}
                    onChange={() => handleNotificationToggle('push')}
                    color="primary"
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Notification Types */}
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Notification Types
            </Typography>

            {[
              { key: 'budgetAlerts', label: 'Budget Alerts', desc: 'Warnings when you approach or exceed budget limits' },
              { key: 'securityAlerts', label: 'Security Alerts', desc: 'Account security events like password changes and login attempts' },
              { key: 'spendingAlerts', label: 'Spending Alerts', desc: 'Large or unusual expense notifications' },
              { key: 'insightAlerts', label: 'AI Insights', desc: 'Personalized spending insights and recommendations' },
              { key: 'agentAlerts', label: 'Agent Notifications', desc: 'Updates from AI agents working on your behalf' },
              { key: 'weeklyReport', label: 'Weekly Report', desc: 'Weekly summary of your financial activity' }
            ].map(({ key, label, desc }) => (
              <Card key={key} elevation={0} sx={{ mb: 1, border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {desc}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notifPrefs[key]}
                      onChange={() => handleNotificationToggle(key)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
        
      case 'account':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Account
            </Typography>
            
            {/* User Info Card */}
            <Card 
              elevation={0} 
              sx={{ 
                mb: 3, 
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      bgcolor: 'primary.main',
                      fontSize: '1.5rem',
                      fontWeight: 700
                    }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {user?.name || 'User'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.email}
                    </Typography>
                    {user?.subscriptionTier && (
                      <Chip 
                        label={user.subscriptionTier} 
                        size="small" 
                        color="primary"
                        sx={{ mt: 0.5, textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <List disablePadding>
                  <ListItemButton onClick={() => navigate('/settings/profile')}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>Profile Settings</Typography>}
                      secondary="Update your personal information"
                    />
                    <ChevronRightIcon color="action" />
                  </ListItemButton>
                  
                  <Divider />
                  
                  <ListItemButton onClick={() => navigate('/settings/email-integration')}>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>Email Integration</Typography>}
                      secondary="Connect email for automatic receipt scanning"
                    />
                    <ChevronRightIcon color="action" />
                  </ListItemButton>
                </List>
              </CardContent>
            </Card>
          </Box>
        );
        
      case 'security':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Security
            </Typography>
            
            <Card 
              elevation={0} 
              sx={{ 
                mb: 2, 
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <List disablePadding>
                  <ListItemButton onClick={() => navigate('/settings/security')}>
                    <ListItemIcon>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>Password & Security</Typography>}
                      secondary="Change password and manage security settings"
                    />
                    <ChevronRightIcon color="action" />
                  </ListItemButton>
                </List>
              </CardContent>
            </Card>

            {/* Biometric Authentication — show on native mobile */}
            {biometric?.isNative && (
              <Card 
                elevation={0} 
                sx={{ 
                  mb: 2, 
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: biometric.isEnabled 
                          ? `${theme.palette.success.main}15` 
                          : `${theme.palette.action.hover}`,
                      }}
                    >
                      {biometric.biometryType === 'face' 
                        ? <FaceIcon sx={{ color: biometric.isEnabled ? 'success.main' : 'text.secondary' }} />
                        : <FingerprintIcon sx={{ color: biometric.isEnabled ? 'success.main' : 'text.secondary' }} />
                      }
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t('biometric.title', 'Biometric Login')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {biometric.isEnabled
                          ? t('biometric.enabledDesc', 'Sign in with {{type}} — active', { 
                              type: biometric.biometryType === 'face' 
                                ? t('biometric.faceId', 'Face Recognition')
                                : t('biometric.fingerprint', 'Fingerprint')
                            })
                          : t('biometric.disabledDesc', 'Use {{type}} for faster sign-in', {
                              type: biometric.biometryType === 'face' 
                                ? t('biometric.faceId', 'Face Recognition')
                                : t('biometric.fingerprint', 'Fingerprint')
                            })
                        }
                      </Typography>
                    </Box>
                    <Switch
                      checked={biometric.isEnabled}
                      onChange={async () => {
                        if (biometric.isEnabled) {
                          await biometric.disableBiometric();
                          setSnackbar({ open: true, message: t('biometric.disabled', 'Biometric login disabled'), severity: 'info' });
                        } else {
                          setBioPassword('');
                          setBioError('');
                          setBioDialogOpen(true);
                        }
                      }}
                      color="success"
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {user?.lastLogin && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Last login:</strong> {new Date(user.lastLogin).toLocaleString()}
                </Typography>
              </Alert>
            )}
          </Box>
        );
        
      case 'data':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Import &amp; Export
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Move your financial data in and out of SoldiKeeper
            </Typography>

            <Stack spacing={2}>
              <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <ImportExportIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Import Transactions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a CSV, OFX, or QIF file exported from your bank or another app (e.g. YNAB).
                        Duplicate transactions are detected automatically.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<ImportExportIcon />}
                        onClick={() => setImportWizardOpen(true)}
                      >
                        Import Transactions
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <ImportExportIcon sx={{ color: 'success.main', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Export Transactions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Download all your transactions as a CSV or JSON file for use in spreadsheets or other apps.
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <ExportButton format="csv" label="Export CSV" />
                        <ExportButton format="json" label="Export JSON" />
                      </Stack>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Stack>

            <ImportWizard
              open={importWizardOpen}
              onClose={() => setImportWizardOpen(false)}
            />
          </Box>
        );

      case 'danger':
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'error.main' }}>
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Irreversible actions that will permanently affect your account
            </Typography>
            
            <Card 
              elevation={0} 
              sx={{ 
                border: `2px solid ${theme.palette.error.main}`,
                bgcolor: theme.palette.mode === 'dark' ? 'error.dark' : 'error.light',
                opacity: 0.9
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <DeleteIcon sx={{ color: 'error.main', mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', mb: 0.5 }}>
                      Delete Account
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{ fontWeight: 600 }}
                    >
                      Delete My Account
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            {/* Learn More Section */}
            <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: 'info.light', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <InfoIcon sx={{ color: 'info.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Need Help?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visit our{' '}
                    <Button
                      size="small"
                      onClick={() => window.open('https://soldikeeper.com/delete-account', '_blank')}
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontWeight: 600 }}
                    >
                      support page
                    </Button>
                    {' '}for more information about account deletion and data privacy.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar Navigation (Desktop) */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto'
          }}
        >
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your preferences
            </Typography>
          </Box>
          
          <List sx={{ p: 2 }}>
            {settingsSections.map((section) => (
              <ListItemButton
                key={section.id}
                selected={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText'
                    }
                  },
                  ...(section.danger && {
                    color: activeSection === section.id ? 'white' : 'error.main',
                    '& .MuiListItemIcon-root': {
                      color: activeSection === section.id ? 'white' : 'error.main'
                    }
                  })
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {section.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={section.title}
                  primaryTypographyProps={{ 
                    variant: 'body2', 
                    fontWeight: activeSection === section.id ? 600 : 500 
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
          {/* Mobile Header */}
          {isMobile && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your preferences
              </Typography>
            </Box>
          )}
          
          {/* Mobile Tab Navigation */}
          {isMobile && (
            <Tabs
              value={activeSection}
              onChange={(e, newValue) => setActiveSection(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                mb: 3,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              {settingsSections.map((section) => (
                <Tab
                  key={section.id}
                  value={section.id}
                  label={section.title}
                  icon={section.icon}
                  iconPosition="start"
                  sx={{
                    ...(section.danger && {
                      color: 'error.main'
                    })
                  }}
                />
              ))}
            </Tabs>
          )}
          
          {/* Section Content */}
          {renderSectionContent()}
        </Container>
      </Box>
      
      {/* Currency Picker Dialog */}
      <Dialog
        open={currencyDialogOpen}
        onClose={() => {
          setCurrencyDialogOpen(false);
          setCurrencySearch('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Select Currency
            </Typography>
            <IconButton
              onClick={() => {
                setCurrencyDialogOpen(false);
                setCurrencySearch('');
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            placeholder="Search currencies..."
            value={currencySearch}
            onChange={(e) => setCurrencySearch(e.target.value)}
            size="small"
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2 }}>
          <Grid container spacing={1.5}>
            {filteredCurrencies.map((curr) => (
              <Grid item xs={6} sm={4} key={curr.code}>
                <Card
                  onClick={() => handleCurrencySelect(curr.code)}
                  sx={{
                    cursor: 'pointer',
                    border: `2px solid ${currency === curr.code ? theme.palette.primary.main : theme.palette.divider}`,
                    position: 'relative',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'scale(1.02)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
                    <Typography variant="h4" sx={{ fontSize: '2rem', mb: 0.5 }}>
                      {curr.flag}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      {curr.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {curr.name}
                    </Typography>
                    {currency === curr.code && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {filteredCurrencies.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No currencies found matching "{currencySearch}"
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteDialogOpen(false);
            setConfirmEmail('');
            setDeleteError('');
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
          {t('settings.deleteAccountConfirm', 'Delete Account')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('settings.deleteWarning', 'This action cannot be undone!')}
            </Typography>
          </Alert>
          
          <DialogContentText sx={{ mb: 2 }}>
            {t('settings.deleteExplanation', 
              'Deleting your account will permanently remove all your data including:'
            )}
          </DialogContentText>
          
          <List dense>
            <ListItem>
              <ListItemText 
                primary="• All transactions and budgets" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• All receipts and documents" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• All split bills and group transactions" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• All personal data and preferences" 
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>

          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            {t('settings.deleteConfirmPrompt', 
              'To confirm, please enter your email address:'
            )}
          </Typography>
          
          <TextField
            fullWidth
            type="email"
            placeholder={user?.email}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            error={!!deleteError}
            helperText={deleteError}
            disabled={isDeleting}
            autoFocus
            sx={{ mb: 1 }}
          />

          <Typography variant="caption" color="text.secondary">
            {t('settings.alternativeMethod', 
              'Alternatively, you can request account deletion by emailing'
            )}{' '}
            <a href="mailto:privacy@soldikeeper.com" style={{ color: theme.palette.primary.main }}>
              privacy@soldikeeper.com
            </a>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setConfirmEmail('');
              setDeleteError('');
            }}
            disabled={isDeleting}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting || !confirmEmail}
            sx={{ minWidth: 120 }}
          >
            {isDeleting ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Biometric enrollment dialog — confirm password to enable */}
      <Dialog
        open={bioDialogOpen}
        onClose={() => !bioLoading && setBioDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {biometric?.biometryType === 'face' 
            ? <FaceIcon color="primary" />
            : <FingerprintIcon color="primary" />
          }
          {t('biometric.enableTitle', 'Enable Quick Login?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('biometric.confirmPasswordDesc', 'Enter your password to enable biometric login. Your credentials will be stored securely in your device\'s hardware vault.')}
          </Typography>
          {bioError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {bioError}
            </Alert>
          )}
          <TextField
            fullWidth
            type="password"
            label={t('auth.password', 'Password')}
            value={bioPassword}
            onChange={(e) => { setBioPassword(e.target.value); setBioError(''); }}
            autoFocus
            disabled={bioLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && bioPassword.length >= 6) {
                handleBioEnroll();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setBioDialogOpen(false)} 
            disabled={bioLoading}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleBioEnroll}
            disabled={bioLoading || bioPassword.length < 6}
            startIcon={biometric?.biometryType === 'face' ? <FaceIcon /> : <FingerprintIcon />}
          >
            {bioLoading ? t('biometric.enabling', 'Setting up...') : t('biometric.enable', 'Enable')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
